import React, { ReactNode, useMemo } from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';

import FootballIcon from '@/assets/icons/footballIcon';
import ShieldIcon from '@/assets/icons/shield';
import { Text } from '@/components/TextComponent';
import { Box, Image, Row } from '@/components/lib/components';
import { designSystem, withOpacity } from '@/styles/styles';
import { League } from '@/types/api.generated';
import { isCollegeSport } from '@/utils/league';
import debounce from 'lodash.debounce';

export type EventCardProps = {
    teamRecords?: [string, string];
    score?: [number, number];
    isLive?: boolean;
    eventTitle: ReactNode | string;
    awayTeamIcon: string;
    homeTeamIcon: string;
    awayTeamShortCode: string;
    homeTeamShortCode: string;
    awayPrimaryTeamColor?: string;
    awaySecondaryTeamColor?: string;
    homePrimaryTeamColor?: string;
    homeSecondaryTeamColor?: string;
    league: League;
    awayScore?: number;
    homeScore?: number;
    variant: 'lobby' | 'eventDetails';
    ballPossession?: 'HOME' | 'AWAY';
    isSelected?: boolean;
    onPress: () => void;
};

// TODO: check with SBK which sports actually can render and subscribe to live scores
export const EventCard = ({
    awayTeamIcon,
    awayTeamShortCode,
    homeTeamIcon,
    homeTeamShortCode,
    teamRecords,
    isLive,
    eventTitle,
    awayPrimaryTeamColor,
    homePrimaryTeamColor,
    awaySecondaryTeamColor,
    homeSecondaryTeamColor,
    awayScore,
    homeScore,
    league,
    isSelected,
    ballPossession,
    variant,
    onPress,
}: EventCardProps) => {
    // keep scores on it's own since we don't want to re render the entire card when the score changes
    const scores = useMemo(() => {
        if (awayScore === undefined || homeScore === undefined || awayScore === null || homeScore === null) {
            return undefined;
        }
        return [awayScore, homeScore];
    }, [awayScore, homeScore]);

    const teamDetails = useMemo(() => {
        return [
            {
                icon: awayTeamIcon,
                primaryColor: awayPrimaryTeamColor,
                secondaryColor: awaySecondaryTeamColor,
                shortCode: awayTeamShortCode,
                showPossession: ballPossession === 'AWAY',
            },
            {
                icon: homeTeamIcon,
                primaryColor: homePrimaryTeamColor,
                secondaryColor: homeSecondaryTeamColor,
                shortCode: homeTeamShortCode,
                showPossession: ballPossession === 'HOME',
            },
        ];
    }, [
        awayPrimaryTeamColor,
        awaySecondaryTeamColor,
        awayTeamIcon,
        awayTeamShortCode,
        ballPossession,
        homePrimaryTeamColor,
        homeSecondaryTeamColor,
        homeTeamIcon,
        homeTeamShortCode,
    ]);

    const shouldHaveFixedScoreWidth = isLive && awayScore !== undefined && homeScore !== undefined;

    // some sports have different max widths for the score
    const scoreWidth = shouldHaveFixedScoreWidth
        ? (leagueToScoreWidth[league] || 18) + SCORE_CONTAINER_PADDING_LEFT
        : 'auto';

    const variantStyle = variantToStyle[variant];

    const onPressWrapper = useMemo(
        () =>
            debounce(
                () => {
                    onPress();
                },
                200,
                { leading: true, trailing: false }
            ),
        [onPress]
    );

    return (
        <TouchableOpacity onPress={onPressWrapper}>
            <Box
                borderWidth={BORDER_WIDTH}
                borderStyle={'solid'}
                style={[
                    variantStyle.containerStyle,
                    isSelected ? styles.selectedContainer : null,
                    { borderColor: variantStyle.borderColor },
                ]}
            >
                <Text
                    variant={'bodySmaller'}
                    color={'gray2'}
                    lineHeight={16}
                    letterSpacing={0}
                    style={isLive ? styles.liveTitle : undefined}
                >
                    {eventTitle}
                </Text>
                <Row>
                    <Box gap={variantStyle.rowGap}>
                        {teamDetails.map(team => (
                            <TeamIcon
                                key={`icons-${team.shortCode}`}
                                icon={team.icon}
                                league={league}
                                primaryColor={team.primaryColor}
                                secondaryColor={team.secondaryColor}
                            />
                        ))}
                    </Box>
                    <Box paddingLeft={variantStyle.shortCodePaddingLeft} gap={variantStyle.rowGap}>
                        {teamDetails.map(team => (
                            <Row alignItems={'center'} key={`shortCode-${team.shortCode}`}>
                                <Text fontSize={13} fontWeight={600} lineHeight={20} letterSpacing={-0.08}>
                                    {team.shortCode}
                                </Text>
                                {variant === 'lobby' ? (
                                    team.showPossession ? (
                                        <Box marginLeft={'s8'}>
                                            <FootballIcon />
                                        </Box>
                                    ) : null
                                ) : null}
                            </Row>
                        ))}
                    </Box>

                    {isLive ? (
                        scores ? (
                            <Box gap={variantStyle.rowGap} paddingLeft={'s12'} width={scoreWidth}>
                                {scores.map((score, index) => (
                                    <Text
                                        key={index}
                                        fontSize={13}
                                        letterSpacing={-0.08}
                                        color={'white'}
                                        textAlign={'right'}
                                        fontWeight={600}
                                        lineHeight={20}
                                    >
                                        {score}
                                    </Text>
                                ))}
                            </Box>
                        ) : null
                    ) : teamRecords ? (
                        <Box gap={variantStyle.rowGap} paddingLeft={'s12'} width={scoreWidth}>
                            {teamRecords?.map((record, index) => (
                                <Text key={index} variant={'bodySmall'} color={'gray3'} letterSpacing={-0.08}>
                                    {record}
                                </Text>
                            ))}
                        </Box>
                    ) : null}
                </Row>
            </Box>
        </TouchableOpacity>
    );
};

const BORDER_WIDTH = 1;

const SCORE_CONTAINER_PADDING_LEFT = 12;

export const TeamIcon = ({
    icon,
    league,
    primaryColor,
    secondaryColor,
    width,
    height,
}: {
    icon: string;
    league: League;
    primaryColor?: string;
    secondaryColor?: string;
    width?: number;
    height?: number;
}) => {
    const iconStyle = useMemo(() => {
        return [styles.teamIcon, { width: width ?? 20, height: height ?? 20 }];
    }, [width, height]);

    if (isCollegeSport(league)) {
        return (
            <ShieldIcon primary={primaryColor} secondary={secondaryColor} width={width ?? 20} height={height ?? 20} />
        );
    }

    return <Image source={{ uri: icon }} style={iconStyle} resizeMode="contain" />;
};

const leagueToScoreWidth: { [key in League]?: number } = {
    [League.Nba]: 26,
    [League.Cbb]: 26,
    [League.Nfl]: 18,
    [League.NflFullSeason]: 18,
    [League.Cfb]: 18,
    [League.Mlb]: 18,
    [League.Nhl]: 18,
};

const styles = StyleSheet.create({
    selectedContainer: {
        backgroundColor: designSystem.colors.gray11,
    },
    teamIcon: {
        width: 20,
        height: 20,
    },

    liveTitle: {
        fontSize: 12,
        color: designSystem.colors.red500,
        fontWeight: 600,
    },
    eventBorderRadius: {
        borderRadius: 16,
        borderCurve: 'continuous',
    },
});

const variantToStyle = {
    lobby: {
        containerStyle: {
            backgroundColor: designSystem.colors.neutral800,
            paddingHorizontal: 16 - BORDER_WIDTH,
            paddingVertical: 12 - BORDER_WIDTH,
            gap: 6,
            ...styles.eventBorderRadius,
        },
        // used for the "isSelected" state
        borderColor: withOpacity(designSystem.colors.white, 0.02),
        rowGap: 's4',
        shortCodePaddingLeft: 's8',
    },
    eventDetails: {
        containerStyle: {
            backgroundColor: designSystem.colors.gray8,
            paddingHorizontal: 12 - BORDER_WIDTH,
            paddingVertical: 10 - BORDER_WIDTH,
            gap: 4,
            ...styles.eventBorderRadius,
        },
        // used for the "isSelected" state
        borderColor: withOpacity(designSystem.colors.white, 0.02),
        rowGap: 's2',
        shortCodePaddingLeft: 's6',
    },
} as const;
